import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";

export interface LoyaltyRewardSelectorItem {
  id: number;
  title: string;
  image?: string | null;
  type?: string;
  status?: string;
  isActive?: boolean;
}

interface LoyaltyRewardSelectorPage {
  items: LoyaltyRewardSelectorItem[];
  totalPages: number;
}

const SELECTOR_PAGE_SIZE = 100;
const LOYALTY_LEVEL_BONUS_SELECTOR_TYPES = ["loyalty", "recurring"] as const;

const getPage = (response: unknown): LoyaltyRewardSelectorPage => {
  const data = response as {
    data?: {
      items?: LoyaltyRewardSelectorItem[];
      totalItems?: number;
      totalPages?: number;
    };
  };
  const items = data.data?.items;
  const totalItems = Number(data.data?.totalItems ?? 0);
  const totalPages = Number(data.data?.totalPages ?? 0);

  return {
    items: Array.isArray(items) ? items : [],
    totalPages: Math.max(
      1,
      Number.isInteger(totalPages) && totalPages > 0
        ? totalPages
        : Math.ceil(totalItems / SELECTOR_PAGE_SIZE),
    ),
  };
};

const getAllSelectorItems = async (
  getUrl: (offset: number) => string,
): Promise<LoyaltyRewardSelectorItem[]> => {
  const firstPage = getPage(await instance.get(getUrl(0)));
  const items = [...firstPage.items];

  for (let offset = 1; offset < firstPage.totalPages; offset += 1) {
    const page = getPage(await instance.get(getUrl(offset)));
    items.push(...page.items);
  }

  const uniqueItems = new Map<number, LoyaltyRewardSelectorItem>();
  items.forEach((item) => {
    if (!uniqueItems.has(item.id)) uniqueItems.set(item.id, item);
  });

  return Array.from(uniqueItems.values());
};

const bonusSelectorUrl = (type: string, offset: number) =>
  `${apiRoutes.bonusesList}?limit=${SELECTOR_PAGE_SIZE}&offset=${offset}&type=${encodeURIComponent(type)}&status=activated`;

export const getLoyaltyBonusSelectorItems = async () => {
  const groups = await Promise.all(
    LOYALTY_LEVEL_BONUS_SELECTOR_TYPES.map((type) =>
      getAllSelectorItems((offset) => bonusSelectorUrl(type, offset)),
    ),
  );
  const uniqueItems = new Map<number, LoyaltyRewardSelectorItem>();
  groups.flat().forEach((item) => {
    if (!uniqueItems.has(item.id)) uniqueItems.set(item.id, item);
  });
  return Array.from(uniqueItems.values());
};

export const getActiveCashbackSelectorItems = () =>
  getAllSelectorItems(
    (offset) =>
      `${apiRoutes.cashbacksList}?limit=${SELECTOR_PAGE_SIZE}&offset=${offset}&isActive=1`,
  );
